/** @file ContextMenuLayer: renders `state.menu`; closes on select, outside click or right-click, and Escape. */
import { chordLabel } from '@zilo/pose3d-shortcuts/chordLabel';
import type { MouseEvent, ReactElement } from 'react';
import type { MenuItem } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { PLATFORM } from './platform';

const MENU_W = 220;
const ITEM_H = 28;

/** Keeps the menu inside the window (opens left/up near the right/bottom edge). */
const placed = (x: number, y: number, items: MenuItem[]): { left: number; top: number } => {
  const h = items.length * ITEM_H + 8;
  const w = globalThis.innerWidth > 0 ? globalThis.innerWidth : x + MENU_W;
  const v = globalThis.innerHeight > 0 ? globalThis.innerHeight : y + h;
  return { left: Math.max(0, x + MENU_W > w ? x - MENU_W : x), top: Math.max(0, y + h > v ? y - h : y) };
};

/**
 * A full-window backdrop under the menu catches the outside click (and a right-click elsewhere, which closes this menu
 * instead of opening the browser's). Escape is the shell's `app.escape` binding, not a listener here.
 */
export function ContextMenuLayer(): ReactElement | null {
  const menu = useEditor((s) => s.menu);
  const closeMenu = useEditor((s) => s.closeMenu);
  if (menu === null) return null;
  const outside = (e: MouseEvent): void => { e.preventDefault(); closeMenu(); };
  return (
    <>
      <div className="p3-menu-backdrop" data-testid="menu-backdrop" onClick={outside} onContextMenu={outside} />
      <div className="p3-menu" role="menu" style={placed(menu.x, menu.y, menu.items)} onContextMenu={(e) => { e.preventDefault(); }}>
        {menu.items.map((item, i) => (item.kind === 'separator'
          ? <div key={`sep-${String(i)}`} className="p3-menu-sep" role="separator" />
          : (
            <button key={`${item.label}-${String(i)}`} type="button" role="menuitem" className="p3-menu-item" disabled={item.disabled}
              onClick={() => { closeMenu(); item.onSelect(); }}>
              <span>{item.label}</span>
              {item.chord === undefined ? null : <span className="p3-muted p3-mono">{chordLabel(item.chord, PLATFORM)}</span>}
            </button>
          )))}
      </div>
    </>
  );
}
